// Import necessary React hooks
import { useRef } from "react";
// Import necessary Material-UI components
import { Button, Grid, Box } from "@mui/material";
// Import custom functions, components and hooks
import { ViewImage, useAuthInfo } from "../../common";
import { QualityReportGroupDetails } from "./QualityReportGroupDetails.component";
import { PageTemplate } from "./PrintTemplate.component";
import { createPDFFileName } from "./functions";
// Import custom CSS
import "./qualityReportGroup.css";
// Import necessary Kendo-React-PDF component
import { PDFExport } from "@progress/kendo-react-pdf";
// Import hook for destructuring data
import { useQualityReportData } from "./hooks/useQualityReportData.hook";

export function QualityReportDetails({ rowData }) {
  // Retrieve decoded authentication information
  const decoded = useAuthInfo();
  // Reference to the PDF export component
  //? Add className="noExport" to any div that you do not wish to export in pdf
  const pdfExportComponent = useRef(null);

  // Function to trigger PDF export when button is clicked.
  const exportPDFWithComponent = () => {
    if (pdfExportComponent.current) {
      pdfExportComponent.current.save();
    }
  };

  // Destructing data and creating groups via hook
  const {
    sampleRequestPageLink,
    itemERPCode,
    receivedItemDescription,
    requesterEmail,
    executiveChefEmail,
    HODDepartment,
    ids,
    dataGroups,
    receivedProductImages,
  } = useQualityReportData(rowData);

  return (
    <Box>
      <div>
        {/* Button to trigger PDF export */}
        <Button
          variant="outlined"
          onClick={exportPDFWithComponent}
          sx={{ mt: 1, mb: 2 }}
        >
          Export Pdf
        </Button>
      </div>
      <div id={ids.qualityReportsID}>
        {/* PDFExport component for exporting content as PDF */}
        {/* Add className="noExport" to any div that you do wish to export in pdf  */}
        <PDFExport
          pageTemplate={PageTemplate}
          ref={pdfExportComponent}
          paperSize="A4"
          margin={{ top: 70, left: 30, right: 30, bottom: 50 }}
          scale={0.6}
          fileName={createPDFFileName(itemERPCode, receivedItemDescription)}
        >
          {/* Content within PDFExport component */}
          <div className="group">
            <b>Identifiers</b>
            <QualityReportGroupDetails data={ids} />
            <div className="row">
              <div className="column">Sample Request ID:</div>
              <div className="column">
                {decoded.email === requesterEmail ||
                decoded.email === executiveChefEmail ||
                decoded.department === HODDepartment ||
                decoded.role === "procurement" ||
                decoded.role === "sampleControl" ||
                decoded.role === "sampleControlView" ? (
                  <a
                    href={sampleRequestPageLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {rowData.sampleRequestID}
                  </a>
                ) : (
                  `${rowData.sampleRequestID}`
                )}
              </div>
            </div>
          </div>

          {/* Mapping through data groups to display various sections */}
          {dataGroups.map((item, index) => {
            return (
              item.data && (
                <div className="group" key={index}>
                  <b>{item.title}</b>
                  <QualityReportGroupDetails data={item.data} />
                </div>
              )
            );
          })}

          {/* Grid container for displaying primary and secondary product images */}
          <Grid container>
            <Grid item xs={12} sm={12} mt={2} mb={2}>
              <b>Product Images</b>
            </Grid>
            {/* Mapping through received product images */}
            {receivedProductImages.map((imageData, index) => {
              return (
                <Grid container key={index}>
                  <Grid item xs={12} sm={5} key={index}>
                    <span>{imageData.title}:</span>
                  </Grid>
                  <Grid item xs={12} sm={7} display="flex">
                    <Grid container>
                      {/* Mapping through individual images or displaying 'pending' */}
                      {imageData.images ? (
                        imageData.images.map((image, index) => {
                          return <ViewImage productImage={image} key={index} />;
                        })
                      ) : (
                        <span>pending</span>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </PDFExport>
      </div>
    </Box>
  );
}
